/**
 * API hooks for managing custom channel tagging rules
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AvailableFilterFieldResponse,
  ChannelTaggingRule,
  ChannelTaggingRuleSet,
  CreateRuleRequest,
  CreateRuleSetRequest,
  UpdateRuleRequest,
  UpdateRuleSetRequest,
} from "@/types/channel-tagging-rules";

const BASE_PATH = "/channel-tagging-rule-sets";

// Query keys
const RULE_SETS_KEY = ["channel-tagging-rule-sets"];
const rulesKey = (ruleSetId: string) => [...RULE_SETS_KEY, ruleSetId, "rules"];

/**
 * Fetch all rule sets
 */
export function useRuleSets() {
  return useQuery<ChannelTaggingRuleSet[]>({
    queryKey: RULE_SETS_KEY,
    queryFn: () => api.get<ChannelTaggingRuleSet[]>(`${BASE_PATH}/`),
  });
}

/**
 * Create a new rule set
 */
export function useCreateRuleSet() {
  const queryClient = useQueryClient();

  return useMutation<ChannelTaggingRuleSet, Error, CreateRuleSetRequest>({
    mutationFn: (data) =>
      api.post<ChannelTaggingRuleSet>(`${BASE_PATH}/create/`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULE_SETS_KEY });
    },
  });
}

/**
 * Update a rule set
 */
export function useUpdateRuleSet() {
  const queryClient = useQueryClient();

  return useMutation<
    ChannelTaggingRuleSet,
    Error,
    { ruleSetId: string; data: UpdateRuleSetRequest }
  >({
    mutationFn: ({ ruleSetId, data }) =>
      api.put<ChannelTaggingRuleSet>(`${BASE_PATH}/${ruleSetId}/`, data),
    onSuccess: (_data, variables) => {
      // Invalidate rulesets so the selector updates with the new default state
      queryClient.invalidateQueries({ queryKey: RULE_SETS_KEY });

      if (variables.data.is_default !== undefined) {
        queryClient.resetQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 2 &&
              queryKey[0] === "datahub" &&
              queryKey[1] === "custom-channels"
            );
          },
        });
      }
    },
  });
}

/**
 * Delete a rule set
 */
export function useDeleteRuleSet() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (ruleSetId) => api.delete<void>(`${BASE_PATH}/${ruleSetId}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULE_SETS_KEY });

      // Clear all custom channels data from cache since deleting a ruleset
      // changes which ruleset is active for channel tagging
      // Query keys are arrays like ["datahub", "custom-channels", "?params"]
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.length >= 2 &&
            queryKey[0] === "datahub" &&
            queryKey[1] === "custom-channels"
          );
        },
      });
    },
  });
}

/**
 * Fetch rules for a specific rule set
 */
export function useRules(ruleSetId: string | null) {
  return useQuery<ChannelTaggingRule[]>({
    queryKey: ruleSetId ? rulesKey(ruleSetId) : [],
    queryFn: () => {
      if (!ruleSetId) return Promise.resolve([]);
      return api.get<ChannelTaggingRule[]>(`${BASE_PATH}/${ruleSetId}/rules/`);
    },
    enabled: !!ruleSetId,
  });
}

/**
 * Create a new rule
 */
export function useCreateRule() {
  const queryClient = useQueryClient();

  return useMutation<
    ChannelTaggingRule,
    Error,
    { ruleSetId: string; data: CreateRuleRequest }
  >({
    mutationFn: ({ ruleSetId, data }) =>
      api.post<ChannelTaggingRule>(
        `${BASE_PATH}/${ruleSetId}/rules/create/`,
        data,
      ),
    onSuccess: (_, { ruleSetId }) => {
      queryClient.invalidateQueries({ queryKey: rulesKey(ruleSetId) });
    },
  });
}

/**
 * Update a rule
 */
export function useUpdateRule() {
  const queryClient = useQueryClient();

  return useMutation<
    ChannelTaggingRule,
    Error,
    { ruleSetId: string; ruleId: string; data: UpdateRuleRequest }
  >({
    mutationFn: ({ ruleSetId, ruleId, data }) =>
      api.put<ChannelTaggingRule>(
        `${BASE_PATH}/${ruleSetId}/rules/${ruleId}/`,
        data,
      ),
    onSuccess: (_, { ruleSetId }) => {
      queryClient.invalidateQueries({ queryKey: rulesKey(ruleSetId) });
    },
  });
}

/**
 * Delete a rule
 */
export function useDeleteRule() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { ruleSetId: string; ruleId: string }>({
    mutationFn: ({ ruleSetId, ruleId }) =>
      api.delete<void>(`${BASE_PATH}/${ruleSetId}/rules/${ruleId}/`),
    onSuccess: (_, { ruleSetId }) => {
      queryClient.invalidateQueries({ queryKey: rulesKey(ruleSetId) });
    },
  });
}

/**
 * Fetch field definitions for rule conditions
 */
export function useRuleFieldDefinitions() {
  return useQuery<AvailableFilterFieldResponse[]>({
    queryKey: ["channel-tagging-rules", "field-definitions"],
    queryFn: () =>
      api.get<AvailableFilterFieldResponse[]>(
        "/channel-tagging-rules/field-definitions/",
      ),
  });
}

/**
 * Reorder rules within a rule set
 */
export function useReorderRules(ruleSetId: string) {
  const queryClient = useQueryClient();

  return useMutation<ChannelTaggingRule[], Error, string[]>({
    mutationFn: (ruleIds) =>
      api.patch<ChannelTaggingRule[]>(`${BASE_PATH}/${ruleSetId}/rules/`, {
        action: "reorder",
        rule_ids: ruleIds,
      }),
    onMutate: async (ruleIds) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: rulesKey(ruleSetId) });

      // Snapshot the previous value
      const previousRules = queryClient.getQueryData<ChannelTaggingRule[]>(
        rulesKey(ruleSetId),
      );

      // Optimistically update to the new value
      if (previousRules) {
        const reorderedRules = ruleIds
          .map((id) => previousRules.find((r) => r.id === id))
          .filter((r): r is ChannelTaggingRule => r !== undefined)
          .map((rule, index) => ({
            ...rule,
            priority: index + 1,
          }));

        queryClient.setQueryData(rulesKey(ruleSetId), reorderedRules);
      }

      // Return context with the snapshot
      return { previousRules };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (
        context &&
        typeof context === "object" &&
        "previousRules" in context
      ) {
        queryClient.setQueryData(rulesKey(ruleSetId), context.previousRules);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync
      queryClient.invalidateQueries({ queryKey: rulesKey(ruleSetId) });
    },
  });
}

/**
 * Export a rule set as JSON
 * Returns the data without automatically downloading it
 */
export function useExportRuleSet() {
  return useMutation<
    { ruleset: { rules: unknown[] } },
    Error,
    { ruleSetId: string }
  >({
    mutationFn: async ({ ruleSetId }) => {
      const data = await api.get<{ ruleset: { rules: unknown[] } }>(
        `${BASE_PATH}/${ruleSetId}/export/`,
      );
      return data;
    },
  });
}

/**
 * Import a rule set from JSON
 */
export function useImportRuleSet() {
  const queryClient = useQueryClient();

  return useMutation<
    ChannelTaggingRuleSet[],
    Error,
    { ruleset: { rules: unknown[] } }
  >({
    mutationFn: (data) =>
      api.post<ChannelTaggingRuleSet[]>(`${BASE_PATH}/import/`, data),
    onSuccess: () => {
      // Invalidate rule sets and rules queries
      queryClient.invalidateQueries({ queryKey: RULE_SETS_KEY });

      // Clear all custom channels data from cache since importing a ruleset
      // changes which ruleset is active for channel tagging
      // Query keys are arrays like ["datahub", "custom-channels", "?params"]
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.length >= 2 &&
            queryKey[0] === "datahub" &&
            queryKey[1] === "custom-channels"
          );
        },
      });
    },
  });
}
